/** Known backend error keywords mapped to i18n keys under `apiErrors`. */
const ERROR_MAP: [RegExp, string][] = [
    [/insufficient.?(credits?|tokens?)/i, 'insufficientCredits'],
    [/not.?found/i, 'notFound'],
    [/already.?exists/i, 'alreadyExists'],
    [/file.?too.?large|payload.?too.?large/i, 'fileTooLarge'],
    [/invalid.?file|unsupported.?file/i, 'invalidFile'],
    [/quota|limit.?exceeded|plan.?limit/i, 'quotaExceeded'],
    [/email.?already|email.?in.?use/i, 'emailInUse'],
    [/unauthorized|unauthenticated/i, 'unauthorized'],
    [/forbidden|access.?denied/i, 'forbidden'],
    [/rate.?limit|too.?many.?requests|throttl/i, 'rateLimited'],
    [/default protection preset/i, 'missingDefaultPreset'],
    [/expired/i, 'expired'],
    [/conflict/i, 'conflict'],
    [/bad.?request|validation/i, 'badRequest'],
    [/server.?error|internal/i, 'serverError'],
];

export async function getApiErrorMessage(response: Response): Promise<string | null> {
    try {
        const cloned = response.clone();
        const payload = (await cloned.json()) as {
            error?: string;
            message?: string;
            title?: string;
            errors?: Record<string, string[] | string>;
        };

        if (payload.errors && typeof payload.errors === 'object') {
            const details = Object.entries(payload.errors)
                .flatMap(([field, value]) => {
                    const messages = Array.isArray(value) ? value : [value];
                    return messages
                        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
                        .map((item) => `${field}: ${item.trim()}`);
                });
            if (details.length > 0) return details.join(' ');
        }

        const msg = payload.error ?? payload.message ?? payload.title;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
    } catch {
        // Not JSON — try plain text.
    }

    try {
        const text = await response.text();
        return text.trim() || null;
    } catch {
        return null;
    }
}

export async function humanizeApiError(
    response: Response,
    t: (key: string, values?: Record<string, string | number>) => string,
    fallback?: string,
): Promise<string> {
    const raw = await getApiErrorMessage(response);

    if (raw) {
        for (const [pattern, key] of ERROR_MAP) {
            if (pattern.test(raw)) {
                return t(key);
            }
        }
    }

    switch (response.status) {
        case 400: return t('badRequest');
        case 401: return t('unauthorized');
        case 403: return t('forbidden');
        case 404: return t('notFound');
        case 409: return t('conflict');
        case 413: return t('fileTooLarge');
        case 429: return t('rateLimited');
        default:
            if (response.status >= 500) return t('serverError');
    }

    return fallback ?? t('generic');
}
